/**
 * Helpers for the "destination first prefetch" data-prop.
 *
 * When a widget is embedded with `data-prop-destination-first` and
 * `data-prop-destination="<slug>"` but no `data-prop-origin`, the destination
 * field resolves its initial slug through `placesUrl()`, which falls through to
 * `?to=<slug>` — a request for the origins that travel to that destination. The
 * destination is never present in its own inbound-origins list, so the lookup
 * silently misses and the field renders blank.
 *
 * `data-prop-destination-first-prefetch` opts that lookup into the unfiltered
 * `?prefetch=true` list instead, so the configured destination resolves.
 */

/**
 * Parses `data-prop-destination-first-prefetch` and gates it on
 * `data-prop-destination-first`.
 *
 * Both values arrive as raw data-prop strings, so the bare-attribute convention
 * used elsewhere in SearchHub (`'' || 'true'`) is applied to each. Resolving the
 * dependency here means every consumer downstream reads a single boolean and
 * never re-checks the coupling.
 *
 * @param {Object} params
 * @param {string|boolean} params.destinationFirst - Raw `destination-first` value
 * @param {string|boolean} params.destinationFirstPrefetch - Raw `destination-first-prefetch` value
 * @returns {boolean} True only when both props are enabled
 */
export function resolveDestinationFirstPrefetch({
  destinationFirst,
  destinationFirstPrefetch,
}) {
  const isEnabled = value => value === '' || value === 'true' || value === true;

  return isEnabled(destinationFirst) && isEnabled(destinationFirstPrefetch);
}

/**
 * Decides whether an initial-slug lookup should skip the origin/destination
 * filters and use the unfiltered places list.
 *
 * The `from` guard is what keeps this safe: when an origin is already selected,
 * `placesUrl()` builds `?from=<origin>` and resolves the slug correctly, so
 * there is nothing to fix. It also prevents the mount-time `fetchTerminals` call
 * (triggered by an already-populated origin) from racing this lookup with a
 * differently-filtered response — both write the same places list.
 *
 * @param {Object} params
 * @param {string} params.field - The autocomplete field ('origin' | 'destination' | 'place')
 * @param {boolean} params.destinationFirstPrefetch - Already-gated config flag
 * @param {string} [params.from] - Currently selected origin slug, if any
 * @returns {boolean} True when the unfiltered places list should be used
 */
export function shouldUseDestinationFirstPrefetchLookup({
  field,
  destinationFirstPrefetch,
  from,
}) {
  return field === 'destination' && Boolean(destinationFirstPrefetch) && !from;
}
